/**
 * Statistical Analysis Service
 *
 * This service implements the data aggregation layer of the framework.
 * It queries the database and transforms raw data into structured insights.
 */

import {
  DataAggregationSpec,
  PerformanceBand,
  TrendClass,
  SignalDetector,
  StatisticalAnalysisInput,
  SignalType,
  FocusArea,
  classifyTrend,
  classifyPerformanceBand,
  classifyVariation,
  calculateTrendConsistency
} from './statisticalAnalysisFramework';

const isDevelopment = process.env.NODE_ENV === 'development';

// Define QueryFunction type locally since it's not exported from context
type QueryFunction = <T = any>(sql: string, params?: any) => Promise<T>;

// ============================================================================
// DATA EXTRACTION QUERIES
// ============================================================================

/**
 * Extract municipality-level KPI data with trends and benchmarks
 */
async function extractMunicipalityKPIData(
  query: QueryFunction,
  municipalityId: string,
  kpiId: string,
  year: number
): Promise<DataAggregationSpec | null> {

  try {
    if (isDevelopment) console.log('[StatisticalAnalysis] Extracting data for:', { municipalityId, kpiId, year });

    // Debug: Check what tables and data exist
    try {
      const tables = await query<Array<{ name: string }>>(`
        SELECT name FROM sqlite_master WHERE type='table' OR type='view' ORDER BY name
      `);
      if (isDevelopment) console.log('[StatisticalAnalysis] Available tables/views:', tables.map(t => t.name));

      const sampleData = await query<Array<{ municipality_id: string; kpi_id: string; year: number; value_t: number }>>(`
        SELECT municipality_id, kpi_id, year, value_t
        FROM gold_kpi_muni_year
        WHERE kpi_id = :kpiId
        LIMIT 5
      `, { kpiId });
      if (isDevelopment) console.log('[StatisticalAnalysis] Sample data for KPI:', sampleData);
    } catch (debugError) {
      if (isDevelopment) console.warn('[StatisticalAnalysis] Debug query failed:', debugError);
    }

    // 1. Get current value + trend for municipality
    const municipalityData = await query<Array<{
      value: number;
      prev_year_value: number | null;
    }>>(`
      WITH muni_trend AS (
        SELECT
          year,
          value_t as value,
          LAG(value_t) OVER (ORDER BY year) as prev_year_value
        FROM gold_kpi_muni_year
        WHERE municipality_id = :municipalityId
          AND kpi_id = :kpiId
          AND year <= :year
        ORDER BY year DESC
        LIMIT 2
      )
      SELECT
        value,
        prev_year_value
      FROM muni_trend
      WHERE year = :year
    `, { municipalityId, kpiId, year });

    if (!municipalityData || municipalityData.length === 0) {
      if (isDevelopment) console.warn('[StatisticalAnalysis] No data found for:', { municipalityId, kpiId, year });
      return null;
    }

    const currentData = municipalityData[0];

    // 2. Get national benchmark (average of all municipalities)
    const nationalBenchmark = await query<Array<{
      national_avg: number;
      national_std: number;
    }>>(`
      SELECT
        AVG(value_t) as national_avg,
        STDDEV(value_t) as national_std
      FROM gold_kpi_muni_year
      WHERE kpi_id = :kpiId
        AND year = :year
    `, { kpiId, year });

    const benchmark = nationalBenchmark[0];

    // 3. Get historical trend (last 5 years)
    const historicalData = await query<Array<{
      year: number;
      value: number;
    }>>(`
      SELECT
        year,
        value_t as value
      FROM gold_kpi_muni_year
      WHERE municipality_id = :municipalityId
        AND kpi_id = :kpiId
        AND year >= :startYear
      ORDER BY year ASC
    `, { municipalityId, kpiId, startYear: year - 4 });

    // 4. Get school-level variation (for equity analysis)
    const schoolVariation = await query<Array<{
      min_value: number;
      max_value: number;
    }>>(`
      SELECT
        MIN(value) as min_value,
        MAX(value) as max_value
      FROM gold_kpi_trend
      WHERE entity_type = 'org_unit'
        AND municipality_id = :municipalityId
        AND kpi_id = :kpiId
        AND year = :year
        AND gender = 'T'
        AND value IS NOT NULL
    `, { municipalityId, kpiId, year });

    // 5. Get gender breakdown (from org unit data aggregated to municipality level)
    const genderData = await query<Array<{
      gender: string;
      value: number;
    }>>(`
      SELECT
        gender,
        AVG(value) as value
      FROM gold_kpi_trend
      WHERE entity_type = 'org_unit'
        AND municipality_id = :municipalityId
        AND kpi_id = :kpiId
        AND year = :year
        AND gender IN ('K', 'M')
      GROUP BY gender
    `, { municipalityId, kpiId, year });

    // ========================================================================
    // TRANSFORM TO FRAMEWORK FORMAT
    // ========================================================================

    // Calculate year-over-year change percentage
    const yoyChange = currentData.value - (currentData.prev_year_value || currentData.value);
    const yoyChangePct = currentData.prev_year_value
      ? (yoyChange / currentData.prev_year_value) * 100
      : 0;

    const trendClass = classifyTrend(yoyChangePct);
    const performanceBand = classifyPerformanceBand(
      currentData.value,
      benchmark.national_avg,
      benchmark.national_std || 1
    );

    const schoolRange = schoolVariation[0] || { min_value: currentData.value, max_value: currentData.value };
    const spread = schoolRange.max_value - schoolRange.min_value;
    const variationLevel = classifyVariation(
      { min: schoolRange.min_value, max: schoolRange.max_value },
      currentData.value
    );

    const consistentYears = calculateTrendConsistency(historicalData);

    let genderGap;
    if (genderData.length === 2) {
      const girls = genderData.find((g: { gender: string; value: number }) => g.gender === 'K')?.value || 0;
      const boys = genderData.find((g: { gender: string; value: number }) => g.gender === 'M')?.value || 0;
      genderGap = {
        girls,
        boys,
        difference: girls - boys
      };
    }

    const aggregatedData: DataAggregationSpec = {
      level: {
        currentValue: currentData.value,
        unit: 'p', // Merit points (default unit for education KPIs)
        performanceBand
      },
      trend: {
        yearOverYearChange: yoyChange,
        yearOverYearChangePct: yoyChangePct,
        trendClass,
        consistentYears,
        historicalValues: historicalData
      },
      benchmark: {
        reference: 'national',
        referenceValue: benchmark.national_avg,
        differenceAbsolute: currentData.value - benchmark.national_avg,
        differencePercent: ((currentData.value - benchmark.national_avg) / benchmark.national_avg) * 100
      },
      variation: {
        schoolRange: {
          min: schoolRange.min_value,
          max: schoolRange.max_value,
          spread
        },
        genderGap,
        variationLevel
      }
    };

    return aggregatedData;

  } catch (error) {
    console.error('Error extracting KPI data:', error);
    return null;
  }
}

// ============================================================================
// MULTI-KPI ANALYSIS
// ============================================================================

/**
 * Analyze multiple KPIs and synthesize insights
 */
export async function analyzeMunicipalityKPIs(
  query: QueryFunction,
  municipalityId: string,
  municipalityName: string,
  year: number,
  kpiIds: string[]
): Promise<StatisticalAnalysisInput | null> {

  if (isDevelopment) console.log('[StatisticalAnalysis] Starting analysis with params:', {
    municipalityId,
    municipalityName,
    year,
    kpiCount: kpiIds.length,
    kpiIds
  });

  try {
    // 1. Extract data for all KPIs
    const kpiDataPromises = kpiIds.map(kpiId =>
      extractMunicipalityKPIData(query, municipalityId, kpiId, year)
    );
    const kpiDataResults = await Promise.all(kpiDataPromises);

    const validResults = kpiDataResults.filter(Boolean);
    if (isDevelopment) console.log('[StatisticalAnalysis] KPI data extraction complete:', {
      requested: kpiIds.length,
      successful: validResults.length,
      failed: kpiIds.length - validResults.length
    });

    // Filter out nulls and get KPI names
    const kpiNamesResult = await query<Array<{
      kpi_id: string;
      kpi_title: string;
    }>>(`
      SELECT DISTINCT kpi_id, kpi_title
      FROM gold_kpi_muni_year
      WHERE kpi_id IN (${kpiIds.map(id => `'${id}'`).join(', ')})
      LIMIT ${kpiIds.length}
    `);

    const kpiNames = Object.fromEntries(
      kpiNamesResult.map((k: { kpi_id: string; kpi_title: string }) => [k.kpi_id, k.kpi_title])
    );

    // 2. Classify each KPI using signal detection
    const classifiedKPIs = kpiIds.map((kpiId, i) => {
      const data = kpiDataResults[i];
      if (!data) return null;

      const signals = SignalDetector.classifyKPI(data);

      return {
        kpiId,
        kpiName: kpiNames[kpiId] || kpiId,
        data,
        signals
      };
    }).filter(Boolean) as Array<{
      kpiId: string;
      kpiName: string;
      data: DataAggregationSpec;
      signals: SignalType[];
    }>;

    // Check if we have enough data to generate analysis
    if (classifiedKPIs.length === 0) {
      if (isDevelopment) console.warn('[StatisticalAnalysis] No valid KPI data found - cannot generate analysis');
      return null;
    }

    if (isDevelopment) console.log('[StatisticalAnalysis] Classification complete:', {
      totalClassified: classifiedKPIs.length,
      signals: classifiedKPIs.map(k => ({ kpi: k.kpiName, signals: k.signals }))
    });

    // 3. Synthesize overall assessment
    const overallStatus = calculateOverallStatus(classifiedKPIs);
    const overallTrend = calculateOverallTrend(classifiedKPIs);
    const systemRisk = calculateSystemRisk(classifiedKPIs);
    const equityLevel = calculateEquityLevel(classifiedKPIs);

    // 4. Extract key findings
    const strengths = classifiedKPIs
      .filter(k => k.signals.includes(SignalType.STRENGTH))
      .map(k => ({
        kpiId: k.kpiId,
        kpiName: k.kpiName,
        signal: SignalType.STRENGTH,
        summary: `${k.kpiName}: ${k.data.level.currentValue.toFixed(1)} ${k.data.level.unit} (${k.data.benchmark.differencePercent > 0 ? '+' : ''}${k.data.benchmark.differencePercent.toFixed(1)}% vs riket)`
      }));

    const risks = classifiedKPIs
      .filter(k =>
        k.signals.includes(SignalType.LATENT_RISK) ||
        k.signals.includes(SignalType.STRUCTURAL_PROBLEM)
      )
      .map(k => ({
        kpiId: k.kpiId,
        kpiName: k.kpiName,
        signal: k.signals.includes(SignalType.STRUCTURAL_PROBLEM) ? SignalType.STRUCTURAL_PROBLEM : SignalType.LATENT_RISK,
        severity: k.signals.includes(SignalType.STRUCTURAL_PROBLEM) ? 'high' : 'medium' as 'high' | 'medium' | 'low',
        summary: `${k.kpiName}: ${k.data.trend.trendClass === TrendClass.DECLINE || k.data.trend.trendClass === TrendClass.STRONG_DECLINE ? 'Nedåtgående trend' : 'Under riksnivå'} (${k.data.benchmark.differencePercent.toFixed(1)}% vs riket)`
      }));

    const opportunities = classifiedKPIs
      .filter(k => k.signals.includes(SignalType.OPPORTUNITY))
      .map(k => ({
        kpiId: k.kpiId,
        kpiName: k.kpiName,
        signal: SignalType.OPPORTUNITY,
        potential: 'high' as 'high' | 'medium' | 'low',
        summary: `${k.kpiName}: Förbättring pågår (+${k.data.trend.yearOverYearChangePct.toFixed(1)}%)`
      }));

    // 5. Generate insights
    const insights = generateInsights(classifiedKPIs);

    // 6. Identify focus areas
    const focusAreas = identifyFocusAreas(classifiedKPIs);

    // 7. Assemble final input for LLM
    const analysisInput: StatisticalAnalysisInput = {
      meta: {
        municipalityId,
        municipalityName,
        analysisDate: new Date().toISOString().split('T')[0],
        academicYear: year,
        comparisonBenchmark: 'national'
      },
      overallAssessment: {
        status: overallStatus,
        trend: overallTrend,
        systemRisk,
        equityLevel
      },
      keyFindings: {
        strengths,
        risks,
        opportunities
      },
      insights,
      focusAreas
    };

    return analysisInput;

  } catch (error) {
    console.error('Error analyzing municipality KPIs:', error);
    return null;
  }
}

// ============================================================================
// SYNTHESIS FUNCTIONS
// ============================================================================

function calculateOverallStatus(
  classifiedKPIs: Array<{ data: DataAggregationSpec; signals: SignalType[] }>
): PerformanceBand {
  const bands = classifiedKPIs.map(k => k.data.level.performanceBand);
  const bandScores = {
    [PerformanceBand.WELL_ABOVE]: 2,
    [PerformanceBand.ABOVE]: 1,
    [PerformanceBand.AT_LEVEL]: 0,
    [PerformanceBand.BELOW]: -1,
    [PerformanceBand.WELL_BELOW]: -2
  };

  const avgScore = bands.reduce((sum, band) => sum + bandScores[band], 0) / bands.length;

  if (avgScore > 1) return PerformanceBand.WELL_ABOVE;
  if (avgScore > 0.5) return PerformanceBand.ABOVE;
  if (avgScore > -0.5) return PerformanceBand.AT_LEVEL;
  if (avgScore > -1) return PerformanceBand.BELOW;
  return PerformanceBand.WELL_BELOW;
}

function calculateOverallTrend(
  classifiedKPIs: Array<{ data: DataAggregationSpec }>
): TrendClass {
  const trends = classifiedKPIs.map(k => k.data.trend.trendClass);
  const trendScores = {
    [TrendClass.STRONG_IMPROVEMENT]: 2,
    [TrendClass.IMPROVEMENT]: 1,
    [TrendClass.STABLE]: 0,
    [TrendClass.DECLINE]: -1,
    [TrendClass.STRONG_DECLINE]: -2
  };

  const avgScore = trends.reduce((sum, trend) => sum + trendScores[trend], 0) / trends.length;

  if (avgScore > 1) return TrendClass.STRONG_IMPROVEMENT;
  if (avgScore > 0.5) return TrendClass.IMPROVEMENT;
  if (avgScore > -0.5) return TrendClass.STABLE;
  if (avgScore > -1) return TrendClass.DECLINE;
  return TrendClass.STRONG_DECLINE;
}

function calculateSystemRisk(
  classifiedKPIs: Array<{ signals: SignalType[] }>
): 'low' | 'moderate' | 'high' {
  const structuralProblems = classifiedKPIs.filter(k =>
    k.signals.includes(SignalType.STRUCTURAL_PROBLEM)
  ).length;
  const latentRisks = classifiedKPIs.filter(k =>
    k.signals.includes(SignalType.LATENT_RISK)
  ).length;

  const totalRisks = structuralProblems * 2 + latentRisks; // Structural problems weigh more
  const riskRatio = totalRisks / classifiedKPIs.length;

  if (riskRatio > 1) return 'high';
  if (riskRatio > 0.5) return 'moderate';
  return 'low';
}

function calculateEquityLevel(
  classifiedKPIs: Array<{ data: DataAggregationSpec; signals: SignalType[] }>
): 'high' | 'moderate' | 'low' {
  const equityRisks = classifiedKPIs.filter(k =>
    k.signals.includes(SignalType.EQUITY_RISK)
  ).length;

  const riskRatio = equityRisks / classifiedKPIs.length;

  if (riskRatio < 0.2) return 'high';
  if (riskRatio < 0.5) return 'moderate';
  return 'low';
}

function generateInsights(
  classifiedKPIs: Array<{ kpiName: string; data: DataAggregationSpec; signals: SignalType[] }>
): StatisticalAnalysisInput['insights'] {
  const performanceHighlights: string[] = [];
  const trendWarnings: string[] = [];
  const equityGaps: string[] = [];

  // Performance highlights (top 2-3 strengths)
  const strengths = classifiedKPIs
    .filter(k => k.signals.includes(SignalType.STRENGTH))
    .sort((a, b) => b.data.benchmark.differencePercent - a.data.benchmark.differencePercent)
    .slice(0, 3);

  strengths.forEach(k => {
    performanceHighlights.push(
      `Stark prestation i ${k.kpiName.toLowerCase()} (+${k.data.benchmark.differencePercent.toFixed(1)}% vs riket)`
    );
  });

  // Trend warnings (declining KPIs)
  const declining = classifiedKPIs
    .filter(k =>
      k.data.trend.trendClass === TrendClass.DECLINE ||
      k.data.trend.trendClass === TrendClass.STRONG_DECLINE
    )
    .sort((a, b) => a.data.trend.yearOverYearChangePct - b.data.trend.yearOverYearChangePct)
    .slice(0, 2);

  declining.forEach(k => {
    trendWarnings.push(
      `Nedåtgående trend i ${k.kpiName.toLowerCase()} (${k.data.trend.yearOverYearChangePct.toFixed(1)}%)`
    );
  });

  // Equity gaps
  const equityIssues = classifiedKPIs.filter(k =>
    k.signals.includes(SignalType.EQUITY_RISK)
  ).slice(0, 2);

  equityIssues.forEach(k => {
    if (k.data.variation.genderGap && Math.abs(k.data.variation.genderGap.difference) > 5) {
      equityGaps.push(
        `Könsskillnad i ${k.kpiName.toLowerCase()} (${Math.abs(k.data.variation.genderGap.difference).toFixed(1)} poäng)`
      );
    }
  });

  return {
    performanceHighlights,
    trendWarnings,
    equityGaps,
    driverInsights: []  // Can be populated if driver analysis is available
  };
}

function identifyFocusAreas(
  classifiedKPIs: Array<{ kpiId: string; kpiName: string; signals: SignalType[] }>
): FocusArea[] {
  const focusAreas: FocusArea[] = [];

  // High priority: Structural problems
  const structuralProblems = classifiedKPIs.filter(k =>
    k.signals.includes(SignalType.STRUCTURAL_PROBLEM)
  );

  if (structuralProblems.length > 0) {
    focusAreas.push({
      area: 'Grundläggande kunskapsnivå',
      priority: 'high',
      signal: SignalType.STRUCTURAL_PROBLEM,
      affectedKpis: structuralProblems.map(k => k.kpiId),
      suggestedAction: 'Genomgång av undervisningsmetoder och elevstöd i berörda ämnen'
    });
  }

  // Medium priority: Equity risks
  const equityRisks = classifiedKPIs.filter(k =>
    k.signals.includes(SignalType.EQUITY_RISK)
  );

  if (equityRisks.length > 0) {
    focusAreas.push({
      area: 'Likvärdighet och inkludering',
      priority: 'medium',
      signal: SignalType.EQUITY_RISK,
      affectedKpis: equityRisks.map(k => k.kpiId),
      suggestedAction: 'Kartläggning av skillnader mellan elevgrupper och skolor'
    });
  }

  // Medium priority: Latent risks
  const latentRisks = classifiedKPIs.filter(k =>
    k.signals.includes(SignalType.LATENT_RISK)
  );

  if (latentRisks.length > 0) {
    focusAreas.push({
      area: 'Trendbrott och förändringsledning',
      priority: 'medium',
      signal: SignalType.LATENT_RISK,
      affectedKpis: latentRisks.map(k => k.kpiId),
      suggestedAction: 'Analys av orsaker till negativ utveckling trots god nuvarande nivå'
    });
  }

  return focusAreas.slice(0, 3); // Max 3 focus areas
}

// ============================================================================
// EXPORTS
// ============================================================================
// Note: All exports are already done inline above with 'export' keyword
